import React from "react";
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useRegressionRuns } from "./useRegressionRuns";
import { SummaryCard } from "./shared";
import { RegressionRunHistoryBar } from "./RegressionRunHistoryBar";
import { RegressionFailureCards } from "./RegressionFailureCards";
import { RegressionFeatureAccordion } from "./RegressionFeatureAccordion";
import { RegressionTagCoverage } from "./RegressionTagCoverage";
import { RegressionSlowestScenarios } from "./RegressionSlowestScenarios";
import { REGRESSION_COLORS } from "./regressionStatus";

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2.5 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", flexWrap: "wrap" }}>
      <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
      {hint && <Typography variant="caption" color="text.secondary" fontFamily="monospace">{hint}</Typography>}
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Paper>
);

export const RegressionTestsPanel: React.FC = () => {
  const { latest, historyBar, loading, error, refresh } = useRegressionRuns();

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Serenity/Cucumber browser suite from <code>ProspoRegressionTestSuite</code>, pushed here by its CI after each run.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refresh}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px" }}
        >
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error">Failed to load regression run data. Try refreshing.</Alert>
      )}

      {!loading && !error && !latest && (
        <Alert severity="info">
          No regression runs yet — results will appear here once CI posts a run from ProspoRegressionTestSuite.
        </Alert>
      )}

      {!loading && !error && latest && (
        <>
          <Alert
            severity={latest.failed > 0 || latest.broken > 0 ? "error" : "success"}
            sx={{ mb: 2.5, alignItems: "center" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography fontWeight={600}>
                {latest.failed > 0 || latest.broken > 0
                  ? `${latest.failed + latest.broken} scenario${latest.failed + latest.broken === 1 ? "" : "s"} failing across ${new Set(latest.scenarios.filter(s => s.result !== "SUCCESS").map(s => s.featurePath)).size} feature(s)`
                  : `All ${latest.totalScenarios} scenarios passing`}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary", flexWrap: "wrap" }}>
                <span>run #{latest.runNumber}</span>
                <span>{new Date(latest.completedAt).toLocaleString()}</span>
                {latest.browser && latest.os && <span>{latest.browser} · {latest.os}</span>}
                <span>{(latest.durationMs / 60000).toFixed(0)}m {Math.round((latest.durationMs % 60000) / 1000)}s</span>
              </Box>
            </Box>
          </Alert>

          <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
            <SummaryCard
              label="Pass rate"
              value={latest.totalScenarios ? `${((latest.passed / latest.totalScenarios) * 100).toFixed(1)}%` : "—"}
              color={REGRESSION_COLORS.passed}
            />
            <SummaryCard label="Passed" value={latest.passed} color={REGRESSION_COLORS.passed} />
            <SummaryCard label="Failed" value={latest.failed} color={latest.failed > 0 ? REGRESSION_COLORS.failed : undefined} />
            <SummaryCard label="Broken" value={latest.broken} color={latest.broken > 0 ? REGRESSION_COLORS.broken : undefined} />
            <SummaryCard label="Skipped" value={latest.skipped} />
            <SummaryCard label="Duration" value={`${(latest.durationMs / 60000).toFixed(1)}m`} />
          </Box>

          <Section title="Run history" hint={`last ${historyBar.length} runs`}>
            <RegressionRunHistoryBar runs={historyBar} />
          </Section>

          <Section title="Needs attention" hint={`${latest.failed + latest.broken} scenario(s)`}>
            <RegressionFailureCards scenarios={latest.scenarios} />
          </Section>

          <Section title="Features" hint={`${new Set(latest.scenarios.map(s => s.featurePath)).size} features · ${latest.totalScenarios} scenarios`}>
            <RegressionFeatureAccordion scenarios={latest.scenarios} />
          </Section>

          <Section title="Coverage by tag" hint="@tag in .feature files">
            <RegressionTagCoverage scenarios={latest.scenarios} />
          </Section>

          <Section title="Slowest scenarios">
            <RegressionSlowestScenarios scenarios={latest.scenarios} />
          </Section>
        </>
      )}
    </Box>
  );
};

export default RegressionTestsPanel;
